import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache, split } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { setContext } from '@apollo/client/link/context';
import { AppContext } from '../app/core/services/app-context.service';
import { environment } from '../environments/environment';
import { inject, Injector } from '@angular/core';
import { CustomAuthService } from '../app/core/services/custom-auth.service';

const wsLink = new GraphQLWsLink(createClient({
    url: environment.nyx.ws,
}));

export function provideGraphQL() {
    return provideApollo(() => {
        // Inject dependencies using inject()
        const httpLink = inject(HttpLink);
        const appContext = inject(AppContext);
        const injector = inject(Injector);

        const context = setContext((operation, { headers, skipAuthorization }) => {
            // Read context values synchronously from signals to avoid
            // async toObservable timing issues during app bootstrap
            const organizationId = appContext.pluck('organizationId');
            const establishmentId = appContext.pluck('establishmentId');

            headers = {
                ...headers,
            };

            // Skip authorization if explicitly requested (e.g., for public queries like invitation acceptance)
            if (!skipAuthorization) {
                // Get auth service lazily using injector to avoid circular dependency
                const authService = injector.get(CustomAuthService);
                const token = authService.getAccessTokenSync();

                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                }
            }

            if (organizationId) {
                headers['X-Organization-ID'] ??= organizationId;
            }

            // A caller can pin a specific establishment via `context: { headers: { 'X-Establishment-ID': ... } }`
            // (e.g. a team captain's requests need the team's own establishment, not the session's active one).
            if (establishmentId) {
                headers['X-Establishment-ID'] ??= establishmentId;
            }

            return {
                headers,
            };
        });

        const splitLink = split(
            ({ query }) => {
                const definition = getMainDefinition(query);
                return (
                    definition.kind === 'OperationDefinition' &&
                    definition.operation === 'subscription'
                );
            },
            wsLink,
            ApolloLink.from([context, httpLink.create({
                uri: environment.nyx.http,
            })]),
        );

        return {
            link: splitLink,
            cache: new InMemoryCache({
                resultCaching: true,
            }),
            devtools: {
                enabled: true,
            },
            useInitialLoading: true,
            // Apollo's default query deduplication merges concurrent requests that share the
            // same query + variables into a single network call — it does not factor in the
            // per-call X-Establishment-ID header override the `context` link above applies
            // (used, for example, when a backoffice establishment combines every establishment's
            // data). Two concurrent requests for the same date range but different establishments
            // would otherwise collapse into one, and every caller — including completely
            // unrelated ones elsewhere on the page — would see whichever establishment's response
            // happened to go out. Disabling deduplication trades an occasional duplicate request
            // for correctness, which matters far more here.
            queryDeduplication: false,
        };
    });
}
